import * as React from 'react';
import { Link } from 'react-router-dom';
import { AppUser } from '../../../../App';
import { Loader } from '../../../../../../components/Loader/Loader';
import { SimpleModal } from '../../../../../../components/SimpleModal/SimpleModal';
import {
  SupportThreadDetail,
  SupportEvidence,
  SupportCandidate,
  SupportReviewRecord,
  SupportSendReceipt,
} from '../../../../../../models/supportEmail';
import { getSupportThread, supportAction, SupportAction } from '../../../../../../services/superadmin/supportEmail';
import {
  ACCESS_DETAILS,
  currentSupportDraft,
  canApproveSupport,
  canSendSupport,
  splitSupportDraft,
  composeSupportDraft,
  supportExpectation,
  supportError,
  supportBadge,
  supportDate,
  sendStatusText,
} from '../../../../../../helpers/supportEmail';

interface Props {
  user: AppUser;
  match: { params: { threadId: string } };
}
interface State {
  detail?: SupportThreadDetail;
  loading: boolean;
  busy: boolean;
  error: string;
  notice: string;
  editing: boolean;
  before: string;
  after: string;
  note: string;
  confirmSend: boolean;
  uncertainDraft?: string;
}
const fieldLabels = {
  guardianName: 'Parent / guardian',
  senderEmail: 'Sender email',
  forename: 'Pupil first name',
  surname: 'Pupil surname',
  fullName: 'Full name as written',
  school: 'School',
  form: 'Class / form',
  birthday: 'Date of birth',
  photoYear: 'Explicit photo year',
  adminNo: 'Admission number',
};
const comparisons = ['schoolMatch', 'firstNameMatch', 'surnameMatch', 'classMatch', 'photoPeriodMatch'];
const comparisonLabels = ['School', 'First name', 'Surname', 'Class / form', 'Photo period'];
function Badge({ value }: { value?: string }) {
  return <span className={'badge badge-' + supportBadge(value)}>{value || 'Not available'}</span>;
}
function Reasons({ values }: { values?: string[] }) {
  return (
    <ul>
      {(values || []).map((value, index) => (
        <li key={index}>{value}</li>
      ))}
    </ul>
  );
}
function Evidence({ value }: { value: SupportEvidence }) {
  return (
    <span>
      <q>{value.excerpt}</q>
      <small className="d-block text-muted">
        Message {value.messageId} · {value.source}
      </small>
    </span>
  );
}
function Candidate({ value }: { value: SupportCandidate }) {
  return (
    <div className="border rounded p-2 mb-2">
      <p>
        {[value.forename, value.surname, value.form].filter(Boolean).join(' · ')}
        <br />
        Student: {value.candidateStudentId || 'No canonical student link'}
      </p>
      <dl className="row">
        {comparisons.map((key, index) => (
          <React.Fragment key={key}>
            <dt className="col-sm-4">{comparisonLabels[index]}</dt>
            <dd className="col-sm-8">{value[key]}</dd>
          </React.Fragment>
        ))}
      </dl>
      <p>
        Images: {(value.candidateImageIds || []).join(', ')}
        <br />
        Jobs: {(value.candidateJobIds || []).join(', ')}
      </p>
      {(value.photoDates || []).map((date, index) => (
        <p key={index}>
          Image {date.imageId}: {supportDate(date.imageJobDate)} · Job {date.jobId}: {supportDate(date.jobDate)}
        </p>
      ))}
      <Reasons values={value.reasons} />
    </div>
  );
}
function History({ row }: { row: SupportReviewRecord }) {
  return (
    <details className="border p-2 mb-2">
      <summary>
        Revision {row.version} · {row.kind} · {row.effectiveState} · {supportDate(row.createdAt)}
      </summary>
      <p>
        Record {row.id}
        <br />
        Draft {row.draftId}
        <br />
        Source message {row.sourceMessageRevision}
        <br />
        Processing {row.sourceProcessingAttemptId}
      </p>
      <p>
        {row.actor && `${row.actor.type}: ${row.actor.userId}`} · {row.reason}
      </p>
      {row.text && <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{row.text}</pre>}
      {row.note && <p>{row.note}</p>}
    </details>
  );
}
export class SupportThread extends React.Component<Props, State> {
  state: State = {
    loading: true,
    busy: false,
    error: '',
    notice: '',
    editing: false,
    before: '',
    after: '',
    note: '',
    confirmSend: false,
  };
  private request = 0;
  private inFlight = false;
  private alive = true;
  componentDidMount() {
    this.load();
  }
  componentDidUpdate(previous: Props) {
    if (previous.match.params.threadId !== this.props.match.params.threadId) {
      this.request++;
      this.setState({ detail: undefined, notice: '', error: '', editing: false, confirmSend: false });
      this.load();
    }
  }
  componentWillUnmount() {
    this.alive = false;
    this.request++;
  }
  load = async (announceChange = true) => {
    const request = ++this.request;
    const id = this.props.match.params.threadId;
    const previous = this.state.detail;
    this.setState({ loading: true, editing: false, confirmSend: false });
    try {
      const detail = await getSupportThread(this.props.user, id);
      if (!this.alive || request !== this.request) return;
      const changed =
        previous &&
        (previous.messageRevision !== detail.messageRevision ||
          previous.processingAttemptId !== detail.processingAttemptId ||
          previous.reviewVersion !== detail.reviewVersion ||
          previous.approvalEventId !== detail.approvalEventId);
      const deliveryChanged =
        previous &&
        detail.sendAttempts.length &&
        previous.sendAttempts.map(row => row.id + ':' + row.state).join(',') !==
          detail.sendAttempts.map(row => row.id + ':' + row.state).join(',');
      this.setState({
        detail,
        loading: false,
        notice:
          changed && announceChange
            ? 'The thread or review changed. Previous edits and approval may no longer be current; review the refreshed evidence.'
            : deliveryChanged
            ? sendStatusText(detail.sendAttempts[0])
            : this.state.notice,
      });
    } catch (error) {
      if (this.alive && request === this.request)
        this.setState({ loading: false, error: supportError(error), detail: undefined });
    }
  };
  beginEdit = () => {
    const split = splitSupportDraft(this.state.detail.currentDraft);
    if (!split.valid) {
      this.setState({ error: 'The system placeholder is invalid. Regenerate the draft instead of editing it.' });
      return;
    }
    this.setState({ editing: true, before: split.before, after: split.after, error: '' });
  };
  act = async (action: SupportAction) => {
    if (this.inFlight || this.state.loading || !this.state.detail) return;
    const detail = this.state.detail;
    if (
      action === 'send' &&
      (!this.state.confirmSend || !canSendSupport(detail) || this.state.uncertainDraft === detail.currentDraft.id)
    )
      return;
    let text;
    try {
      if (action === 'edit')
        text = composeSupportDraft(this.state.before, this.state.after, detail.currentDraft.outcome === 'MATCH');
    } catch (error) {
      this.setState({ error: error.message });
      return;
    }
    this.inFlight = true;
    const id = detail.id;
    this.setState({ busy: true, error: '', notice: '', confirmSend: false });
    try {
      const response = await supportAction(
        this.props.user,
        id,
        detail.currentDraft && detail.currentDraft.id,
        action,
        supportExpectation(detail),
        { text, note: this.state.note }
      );
      if (!this.alive || id !== this.props.match.params.threadId) return;
      const receipt = action === 'send' ? (response as SupportSendReceipt) : undefined;
      this.setState({
        editing: false,
        note: '',
        notice: receipt
          ? sendStatusText(receipt)
          : action === 'edit'
          ? 'Saved a new draft revision. Any previous approval is invalidated.'
          : 'Review action recorded: ' + action + '.',
        ...(receipt && receipt.state === 'UNKNOWN' ? { uncertainDraft: detail.currentDraft.id } : {}),
      });
      await this.load(false);
    } catch (error) {
      if (!this.alive || id !== this.props.match.params.threadId) return;
      const uncertain =
        action === 'send' &&
        (!error.response ||
          (error.response.status >= 500 &&
            !['SUPPORT_SENDING_DISABLED', 'SUPPORT_SEND_REQUIRES_NODE_20'].includes(
              error.response.data && error.response.data.details && error.response.data.details.text
            )));
      this.setState({
        error: uncertain
          ? 'The send response was not confirmed. Do not resend; refresh and investigate the attempt before reconciliation.'
          : supportError(error),
        ...(uncertain ? { uncertainDraft: detail.currentDraft.id } : {}),
      });
      await this.load(false);
    } finally {
      this.inFlight = false;
      if (this.alive) this.setState({ busy: false });
    }
  };
  render() {
    const {
      detail,
      loading,
      busy,
      error,
      notice,
      editing,
      before,
      after,
      note,
      confirmSend,
      uncertainDraft,
    } = this.state;
    const result = detail && detail.processing && detail.processing.result;
    const match = result && result.matchingResult;
    const draft = detail && detail.currentDraft;
    const current = detail && currentSupportDraft(detail);
    const fenced =
      detail && (detail.sendAttempts || []).some(row => ['UNKNOWN', 'PENDING', 'SENDING'].includes(row.state));
    const disabled = busy || loading || !!fenced;
    return (
      <section className="py-3">
        <div className="d-flex justify-content-between mb-3">
          <Link to="/supportEmails">← Support Emails</Link>
          <button
            className="btn btn-secondary"
            disabled={busy || loading}
            onClick={() => {
              this.setState({ error: '' });
              this.load();
            }}
          >
            {editing ? 'Refresh (discard edits)' : 'Refresh'}
          </button>
        </div>
        <h2>Support thread</h2>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}
        {notice && (
          <div className="alert alert-info" role="status">
            {notice}
          </div>
        )}
        {loading && <Loader />}
        {detail && !loading && (
          <>
            <p className="text-break">
              {detail.subject || '(no subject)'}
              <br />
              From: {detail.sender} · Latest: {supportDate(detail.latestMessageAt)}
            </p>
            <p>
              Classification: <Badge value={detail.classification} /> · Processing:{' '}
              <Badge value={detail.processingStatus} /> · Review: <Badge value={detail.reviewState} />
            </p>
            {(detail.requiresManualReview ||
              (draft && draft.internalSummary && draft.internalSummary.requiresInvestigation)) && (
              <div className="alert alert-warning" role="alert">
                <strong>Manual investigation required. This case cannot be approved as a clean match.</strong>
                <Reasons values={detail.riskFlags} />
                <Reasons values={draft && draft.internalSummary && draft.internalSummary.flags} />
              </div>
            )}
            {fenced && (
              <div className="alert alert-warning">
                Sending is pending or uncertain. Investigate the send history; do not submit another reply.
              </div>
            )}
            <h3>Customer correspondence</h3>
            {detail.messages
              .slice()
              .sort((a, b) => new Date(a.ingestedAt).getTime() - new Date(b.ingestedAt).getTime())
              .map(message => (
                <article className="card mb-3" key={message.id}>
                  <div className="card-body">
                    <strong>{message.subject || '(no subject)'}</strong>
                    <p>
                      From {message.sender} · {supportDate(message.receivedAt || message.ingestedAt)}
                      <br />
                      <small>Message {message.id}</small>
                    </p>
                    <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{message.textBody}</pre>
                    {message.textTruncated && (
                      <p className="text-warning">Source text is truncated in this review view.</p>
                    )}
                    <Reasons values={message.issues} />
                  </div>
                </article>
              ))}
            {detail.moreMessages && (
              <p className="text-warning">
                Only the latest 20 messages are included. Older correspondence requires operator investigation.
              </p>
            )}
            <h3>AI extraction</h3>
            {detail.processing && (
              <p>
                Model {detail.processing.model} · Prompt {detail.processing.promptVersion} ·{' '}
                {supportDate(detail.processing.processedAt)}
                <br />
                Tokens: input{' '}
                {detail.processing.usage.inputTokens === undefined ? '—' : detail.processing.usage.inputTokens}, output{' '}
                {detail.processing.usage.outputTokens === undefined ? '—' : detail.processing.usage.outputTokens}, total{' '}
                {detail.processing.usage.totalTokens === undefined ? '—' : detail.processing.usage.totalTokens}
                {detail.processing.errorCode && (
                  <span className="d-block text-danger">{detail.processing.errorCode}</span>
                )}
              </p>
            )}
            {result ? (
              <>
                <p>
                  Processing outcome: <Badge value={result.outcome} />
                </p>
                <div className="alert alert-secondary">
                  Missing required fields:{' '}
                  {result.missingFields.length ? result.missingFields.join(', ') : 'None reported'}
                </div>
                <div className="table-responsive">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Extracted value</th>
                        <th>Source evidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.keys(fieldLabels).map(key => {
                        const field = result.extraction.fields[key];
                        return (
                          <tr key={key}>
                            <th>{fieldLabels[key]}</th>
                            <td>{field ? field.value : 'Not supplied'}</td>
                            <td>{field && <Evidence value={field.evidence} />}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <details>
                  <summary>Classification evidence</summary>
                  {result.extraction.classificationEvidence.map((value, index) => (
                    <p key={index}>
                      <Evidence value={value} />
                    </p>
                  ))}
                </details>
                <Reasons values={result.reasons} />
              </>
            ) : (
              <p>No current extraction result. Refresh after processing completes.</p>
            )}
            <h3>Deterministic matching</h3>
            {match ? (
              <>
                <p>
                  <Badge value={match.status} />
                </p>
                <Candidate value={match} />
                <p>
                  Account surname risk: <Badge value={match.accountSurnameRisk.status} /> · History:{' '}
                  {match.accountSurnameRisk.historyState}
                  <br />
                  Distinct verified surnames: {match.accountSurnameRisk.distinctVerifiedSurnameCount} · Surname already
                  associated: {String(match.accountSurnameRisk.surnameAlreadyAssociated)}
                </p>
                <Reasons values={match.accountSurnameRisk.reasons} />
                <p>
                  Policy {match.evidence.policyVersion} · Schools {match.evidence.schoolIds.join(', ')}
                </p>
                {match.evidence.photoPeriod && (
                  <p>
                    Photo period {match.evidence.photoPeriod.id}: {supportDate(match.evidence.photoPeriod.start)} to{' '}
                    {supportDate(match.evidence.photoPeriod.endExclusive)} (exclusive)
                  </p>
                )}
                <details>
                  <summary>Candidate and rejected-candidate evidence</summary>
                  {match.evidence.candidates.concat(match.evidence.rejectedCandidates).map((value, index) => (
                    <Candidate key={index} value={value} />
                  ))}
                </details>
              </>
            ) : (
              <p>No matching result for this processing revision.</p>
            )}
            <h3>Proposed reply</h3>
            <p>
              Source message revision {detail.messageRevision}
              <br />
              Processing attempt {detail.processingAttemptId || 'Not current'} · Review version {detail.reviewVersion}
            </p>
            <button
              className="btn btn-primary mb-3"
              disabled={disabled || detail.processingStatus !== 'PROCESSED' || !detail.processingAttemptId || editing}
              onClick={() => this.act('generate')}
            >
              {draft ? 'Regenerate draft' : 'Generate draft'}
            </button>
            {draft && (
              <div className="card mb-3">
                <div className="card-body">
                  <p>
                    Draft revision {draft.version} · <Badge value={draft.effectiveState} />
                    <br />
                    Draft ID {draft.id}
                    <br />
                    Method {draft.generationMethod} · {draft.promptVersion}
                  </p>
                  {draft.outcome === 'MATCH' && (
                    <p className="alert alert-secondary">
                      <code>{ACCESS_DETAILS}</code> is a protected system placeholder. Access details are resolved only
                      by the backend after approval.
                    </p>
                  )}
                  {editing ? (
                    <form
                      onSubmit={event => {
                        event.preventDefault();
                        this.act('edit');
                      }}
                    >
                      <label htmlFor="support-draft-before">
                        {draft.outcome === 'MATCH' ? 'Reply before access details' : 'Reply text'}
                      </label>
                      <textarea
                        id="support-draft-before"
                        className="form-control mb-2"
                        rows={5}
                        maxLength={4000}
                        value={before}
                        disabled={busy}
                        onChange={event => this.setState({ before: event.target.value })}
                      />
                      {draft.outcome === 'MATCH' && (
                        <>
                          <p>
                            <code>{ACCESS_DETAILS}</code> (fixed)
                          </p>
                          <label htmlFor="support-draft-after">Reply after access details</label>
                          <textarea
                            id="support-draft-after"
                            className="form-control mb-2"
                            rows={3}
                            maxLength={4000}
                            value={after}
                            disabled={busy}
                            onChange={event => this.setState({ after: event.target.value })}
                          />
                        </>
                      )}
                      <button className="btn btn-primary mr-2" disabled={busy} type="submit">
                        Save new revision
                      </button>
                      <button
                        className="btn btn-secondary"
                        disabled={busy}
                        type="button"
                        onClick={() => this.setState({ editing: false })}
                      >
                        Cancel edit
                      </button>
                    </form>
                  ) : (
                    <>
                      <pre style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                        {draft.text || 'No customer-ready reply. Manual investigation is required.'}
                      </pre>
                      <button
                        className="btn btn-outline-primary mr-2"
                        disabled={disabled || !current}
                        onClick={this.beginEdit}
                      >
                        Edit draft
                      </button>
                      <button
                        className="btn btn-success mr-2"
                        disabled={disabled || !canApproveSupport(detail)}
                        onClick={() => this.act('approve')}
                      >
                        Approve exact revision
                      </button>
                      <button
                        className="btn btn-outline-danger"
                        disabled={disabled || !current || detail.reviewState === 'REJECTED'}
                        onClick={() => this.act('reject')}
                      >
                        Reject
                      </button>
                      <label htmlFor="support-review-note" className="d-block mt-2">
                        Optional rejection note (internal)
                      </label>
                      <input
                        id="support-review-note"
                        className="form-control"
                        maxLength={500}
                        value={note}
                        disabled={disabled}
                        onChange={event => this.setState({ note: event.target.value })}
                      />
                      {canSendSupport(detail) && uncertainDraft !== draft.id && (
                        <button
                          className="btn btn-warning mt-3"
                          disabled={disabled}
                          onClick={() => this.setState({ confirmSend: true })}
                        >
                          Send approved reply
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}
            <h3>Send history</h3>
            {(detail.sendAttempts || []).length === 0 && <p>No send attempts.</p>}
            {(detail.sendAttempts || []).map(row => (
              <div key={row.id} className={'alert alert-' + supportBadge(row.state)}>
                <strong>{sendStatusText(row)}</strong>
                <p>
                  To {row.recipient} · Draft {row.approvedDraftId}
                  <br />
                  Attempt {row.id} · Message-ID {row.messageId}
                  <br />
                  SMTP: {row.deliveryState} · Sent-folder append: {row.appendState} ·{' '}
                  {supportDate(row.finishedAt || row.submittedAt)}
                </p>
                {row.errorCode && <p>{row.errorCode}</p>}
              </div>
            ))}
            {detail.moreSendAttempts && <p>Additional send history exists beyond this view.</p>}
            <h3>Review audit history</h3>
            {detail.reviewHistory.map(row => (
              <History key={row.id} row={row} />
            ))}
            {detail.moreReviewHistory && <p>Older review history exists beyond this view.</p>}
            {detail.generationFailures.length > 0 && (
              <details>
                <summary>Draft generation failures</summary>
                {detail.generationFailures.map(row => (
                  <History key={row.id} row={row} />
                ))}
              </details>
            )}
            <SimpleModal
              isOpen={confirmSend && !busy}
              title="Send this approved reply?"
              buttonText="Send exact approved revision"
              buttonCancelText="Cancel"
              onButtonClick={() => this.act('send')}
              onCloseClick={() => this.setState({ confirmSend: false })}
            >
              <p>
                This submits one manually approved reply to {detail.sender}. No photo access details are loaded into
                this browser.
              </p>
              <p>
                Draft {draft && draft.id} · Review version {detail.reviewVersion}. Confirm that this is the intended
                recipient and revision.
              </p>
            </SimpleModal>
          </>
        )}
      </section>
    );
  }
}
